// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import SharedHeader from '../components/SharedHeader';
// import SharedFooter from '../components/SharedFooter';
// import UserBookings from './userRide/UserBookings';

// export default function UserDashboard() {
//   const navigate = useNavigate();
//   const [user, setUser] = useState(null);
//   const [activeTab, setActiveTab] = useState('active');
//   const [loading, setLoading] = useState(true);

//   useEffect(() => {
//     const storedUser = localStorage.getItem('user');
//     if (storedUser) {
//       setUser(JSON.parse(storedUser));
//     } else {
//       navigate('/login');
//     }
//     setLoading(false);
//   }, [navigate]);

//   if (loading || !user) {
//     return (
//       <div className="flex items-center justify-center h-screen">
//         <p className="text-lg text-gray-600">Loading user...</p>
//       </div>
//     );
//   }

//   return (
//     <>
//       <SharedHeader />

//       <main className="max-w-5xl mx-auto p-6">
//         <h1 className="text-3xl font-semibold mb-6">Welcome, {user.userName || user.userEmail}!</h1>

//         {/* Tabs */}
//         <div className="flex gap-4 mb-6 flex-wrap">
//           <TabButton label="Active Rides" active={activeTab === 'active'} onClick={() => setActiveTab('active')} />
//           <TabButton label="Pending Rides" active={activeTab === 'pending'} onClick={() => setActiveTab('pending')} />
//           <TabButton label="Cancelled Rides" active={activeTab === 'cancelled'} onClick={() => setActiveTab('cancelled')} />
//           <TabButton label="Book a Ride" active={activeTab === 'book'} onClick={() => setActiveTab('book')} />
//         </div>

//         {/* Tab content */}
//         <div className="bg-white shadow p-6 rounded-lg min-h-[300px]">
//           {activeTab === 'book' ? (
//             <BookRideForm />
//           ) : (
//             <UserBookings status={activeTab} />
//           )}
//         </div>
//       </main>

//       <SharedFooter />
//     </>
//   );
// }

// // ✅ TabButton component
// function TabButton({ label, active, onClick }) {
//   return (
//     <button
//       className={`px-4 py-2 rounded-lg font-medium transition ${
//         active ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
//       }`}
//       onClick={onClick}
//     >
//       {label}
//     </button>
//   );
// }

// // ✅ BookRideForm placeholder
// function BookRideForm() {
//   const navigate = useNavigate();
//   return (
//     <div>
//       <p>Ready to book your next ride?</p>
//       <button
//         className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
//         onClick={() => navigate('/book-ride')}
//       >
//         Go to Booking Page
//       </button>
//     </div>
//   );
// }

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircularProgress, Box, Typography } from '@mui/material';
import SharedHeader from '../components/SharedHeader';
import SharedFooter from '../components/SharedFooter';
import UserBookings from './userRide/UserBookings';
import { tokenStorage } from '../utils/tokenStorage';
import { useAuth } from '../components/AuthContext';
import { getUserBookings } from '../services/api';

const BOOKING_STATUS = {
  PENDING: 0,
  CONFIRMED: 1,
  CANCELLED: 2,
  COMPLETED: 3,
};

const TAB_TO_STATUS = {
  upcoming: BOOKING_STATUS.CONFIRMED,
  pending: BOOKING_STATUS.PENDING,
  completed: BOOKING_STATUS.COMPLETED,
  cancelled: BOOKING_STATUS.CANCELLED,
};

export default function UserDashboard() {
  const navigate = useNavigate();
  const { userId } = useAuth();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [loading, setLoading] = useState(true);

  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(true);
  const [bookingsError, setBookingsError] = useState(null);

  useEffect(() => {
    const storedUser = tokenStorage.getUser();
    const hasToken = tokenStorage.isLoggedIn();

    if (storedUser && hasToken) {
      setUser(storedUser);
    } else {
      navigate('/');
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (!userId) {
      setBookingsLoading(false);
      return;
    }

    setBookingsLoading(true);
    setBookingsError(null);

    getUserBookings(userId)
      .then((res) => {
        if (res.data.status === 200 && Array.isArray(res.data.data)) {
          // Filter out invalid bookings at the source
          const valid = res.data.data.filter((b) => b && b.bookingId != null);
          console.log('📋 Valid bookings:', valid.length, 'of', res.data.data.length);
          setBookings(valid);
        } else {
          console.error('Unexpected response:', res.data);
          setBookingsError('Could not load bookings');
        }
      })
      .catch((err) => {
        console.error('Error fetching user bookings:', err);
        setBookingsError('Failed to load bookings');
      })
      .finally(() => setBookingsLoading(false));
  }, [userId]);

  const counts = {
    upcoming: bookings.filter((r) => r.isRideConfirmed === BOOKING_STATUS.CONFIRMED).length,
    pending: bookings.filter((r) => r.isRideConfirmed === BOOKING_STATUS.PENDING).length,
    completed: bookings.filter((r) => r.isRideConfirmed === BOOKING_STATUS.COMPLETED).length,
    cancelled: bookings.filter((r) => r.isRideConfirmed === BOOKING_STATUS.CANCELLED).length,
  };

  if (loading || !user) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <SharedHeader />

      <main className="max-w-5xl mx-auto p-6">
        <h1 className="text-3xl font-semibold mb-6">
          Welcome, {user.userName || user.userEmail}!
        </h1>

        <div className="flex gap-3 mb-6 flex-wrap">
          <TabButton
            label="Upcoming"
            count={counts.upcoming}
            active={activeTab === 'upcoming'}
            onClick={() => setActiveTab('upcoming')}
          />
          <TabButton
            label="Awaiting Confirmation"
            count={counts.pending}
            active={activeTab === 'pending'}
            onClick={() => setActiveTab('pending')}
          />
          <TabButton
            label="Completed"
            count={counts.completed}
            active={activeTab === 'completed'}
            onClick={() => setActiveTab('completed')}
          />
          <TabButton
            label="Cancelled"
            count={counts.cancelled}
            active={activeTab === 'cancelled'}
            onClick={() => setActiveTab('cancelled')}
          />
          <TabButton
            label="Book a Ride"
            active={activeTab === 'book'}
            onClick={() => setActiveTab('book')}
          />
        </div>

        <div className="bg-white shadow p-6 rounded-lg min-h-[300px]">
          {activeTab === 'book' ? (
            <BookRideForm />
          ) : (
            <UserBookings
              status={activeTab}
              bookings={bookings}
              loading={bookingsLoading}
              error={bookingsError}
            />
          )}
        </div>
      </main>

      <SharedFooter />
    </>
  );
}

function TabButton({ label, count, active, onClick }) {
  return (
    <button
      className={`px-4 py-2 rounded-lg font-medium transition flex items-center gap-2 ${
        active
          ? 'bg-blue-600 text-white'
          : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
      }`}
      onClick={onClick}
    >
      <span>{label}</span>
      {count !== undefined && count > 0 && (
        <span
          className={`text-xs rounded-full px-2 py-0.5 font-semibold ${
            active ? 'bg-white text-blue-600' : 'bg-blue-600 text-white'
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function BookRideForm() {
  const navigate = useNavigate();
  return (
    <div>
      <p>Ready to book your next ride?</p>
      <button
        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        onClick={() => navigate('/book-ride')}
      >
        Go to Booking Page
      </button>
    </div>
  );
}

export { BOOKING_STATUS, TAB_TO_STATUS };